import React, { useEffect, useReducer } from 'react';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import consts from '../consts';
import strings from '../Globalization';
import connectionService from '../services/Connection';
import util from '../util/index';
import ReportForm from './forms/ReportForm';

const reportType = {
	BIHistoryReport: consts.isNodeServer ? '6123c4f879f0fa4c6ccab64a' : 149,
	EquipmentUsageReport: consts.isNodeServer ? '6123c4f879f0fa4c6ccab64b' : 150,
	CycleUsageReport: consts.isNodeServer ? '6123c4f879f0fa4c6ccab64c' : 151,
	TaskHistoryReport: consts.isNodeServer ? '6123c4f879f0fa4c6ccab64d' : 152
};

const Report = ({ 
	dataProperty ='topFilterList',
	comboTypes = [
		'Location',
		'Incubator',
		'BILotNumber',
		'TestOrControl',
		'ReportType',
		'EquipmentFamily',
		'Equipment',
		'Model',
		'SterlizerType',
		'EquipmentType',
		'IncubatorName',
		'Sterilizer',
		'BIResult'
	] }) => {
	const [state, setState] = useReducer(
		(prev, next) => (typeof next === 'function' ? { ...prev, ...next(prev) } : { ...prev, ...next }),
		{
			loading: false,
			loadingMessage: strings.DefaultWaitingMsg,
			dateTimeFormat: util.getDateTimeFormat(),
			startDate: dayjs().subtract(1, 'month'),
			endDate: new Date(),
			isBIHistory: false,
			isOtherReportType: true,
			isDefault: true,
			isEquipmentUsage: false,
			reportOptions: [],
			activeRecord: { record: {}, combos: {} },
			equipmentOptions: []
		}
	);

	const handleSubmit = (values, { resetForm }) => {
		if (values.Report === reportType.EquipmentUsageReport && (!values.EquipmentId || values.EquipmentId === '')) {
			Swal.fire({
				titleText: strings.ErrorTitle,
				text: strings.SelectEquipment,
				icon: 'error',
				confirmButtonText: strings.Ok,
				confirmButtonColor: '#3085d6',
				allowOutsideClick: false
			});
			setState({ loading: false });
			return;
		}
		if (values.Format === "XLS") {
			Swal.fire({
				titleText: strings.WarningTitle,
				text: strings.excelPopMsg,
				icon: 'warning',
				confirmButtonText: strings.Ok,
				confirmButtonColor: '#3085d6',
				allowOutsideClick: false
			});
		};
		setState({ loading: true });
		let sterilizerName, sterlizerTypeComboData, sterilizerIndex;
		if (values.SterilizerId !== 0 && values.SterilizerId) {
			sterlizerTypeComboData = state.activeRecord.combos.SterlizerType;
			sterilizerIndex = sterlizerTypeComboData.findIndex(
				(item) => item.LookupId === (consts.isNodeServer ? values.SterilizerId : Number(values.SterilizerId))
			);
			sterilizerName = sterlizerTypeComboData[sterilizerIndex]?.DisplayValue;
		}
		let reportCriteria = {
			FromDate: dayjs(values.From).format('DD/MMM/YY'),
			ToDate: dayjs(values.To).format('DD/MMM/YY'),
			IncubatorId: consts.isNodeServer
				? values.IncubatorId
				: isNaN(parseInt(values.IncubatorId))
					? 0
					: values.IncubatorId,
			SterilizerId: consts.isNodeServer
				? values.SterilizerId
				: isNaN(parseInt(values.SterilizerId))
					? 0
					: values.SterilizerId,
			SterilizerName: sterilizerName,
			BIResult: consts.isNodeServer ? values.BIResult : isNaN(parseInt(values.BIResult)) ? 0 : values.BIResult,
			EquipmentId: consts.isNodeServer
				? values.EquipmentId
				: isNaN(parseInt(values.EquipmentId))
					? 0
					: parseInt(values.EquipmentId),
			EquipmentTypeId: consts.isNodeServer
				? values.EquipmentTypeId
				: isNaN(parseInt(values.EquipmentTypeId))
					? 0
					: parseInt(values.EquipmentTypeId)
		};

		let report = consts.isNodeServer ? values.Report : parseInt(values.Report);
		let format = values.Format;

		const urls = {
			[reportType.EquipmentUsageReport]: connectionService.EquipmentUsesReport,
			[reportType.CycleUsageReport]: connectionService.CycleUsageReport,
			[reportType.TaskHistoryReport]: connectionService.TaskHistoryReport
		};
		const url = urls[report] || connectionService.BIHistoryReport;

		let formData = new FormData();
		formData.append('reportCriteria', JSON.stringify(reportCriteria));
		formData.append('format', format);

		fetch(url, { method: 'POST', body: formData, credentials: 'include' })
			.then((response) => response.text())
			.then((responseText) => {
				if (responseText === 'false' || responseText === '') {
					Swal.fire({
						titleText: strings.ErrorTitle,
						text: strings.RecordNotFound,
						icon: 'error',
						confirmButtonText: strings.Ok,
						confirmButtonColor: '#3085d6',
						allowOutsideClick: false
					});
				} else {
					window.open(
						strings.formatString(
							'{0}?reportCriteria={1}&format={2}',
							`${consts.hostURL.URL}${url}`,
							JSON.stringify(reportCriteria),
							format
						),
						'_blank'
					);
				}
			})
			.catch((error) => {
				setState({ loading: true });
				Swal.fire({
					titleText: strings.ErrorTitle,
					text: strings.RecordNotFound,
					icon: 'error',
					confirmButtonText: strings.Ok,
					confirmButtonColor: '#3085d6',
					allowOutsideClick: false
				});
			})
			.finally(() => setState({ loading: false }));
	};

	const fetchData = () => {
		setState({ loading: true });
		const formData = new FormData();
		const combosArr = comboTypes?.map((comboType) => ({ type: comboType, loaded: true }));
		formData.append('comboTypes', JSON.stringify(combosArr));
		formData.append('action', 'load');
		formData.append('id', '0');
		fetch(connectionService.EquipmentReport, { method: 'POST', body: formData })
			.then((res) => res.json())
			.then(({ success, data, combos }) => {
				if (success) {
					const reportOptions = combos.ReportType.map((entry) => ({
						id: entry.LookupId,
						label: entry.DisplayValue
					}));
					const equipmentOptions = combos.Equipment.map((entry) => ({
						id: entry.LookupId,
						label: entry.LookupId
					}));
					const equipmentTypeOptions = combos.EquipmentType.map((entry) => ({
						id: entry.LookupId,
						label: entry.DisplayValue
					}));
					const incubatorOptions = combos.Incubator.map((entry) => ({
						id: entry.LookupId,
						label: entry.DisplayValue
					}));
					const sterilizerOptions = combos.SterlizerType?.map((entry) => ({
						id: entry.LookupId,
						label: entry.DisplayValue
					}));
					const biResultOptions = combos.BIResult.map((entry) => ({
						id: entry.LookupId,
						label: entry.DisplayValue
					}));
					delete data.EquipmentId
					setState({
						activeRecord: { record: data, combos },
						reportOptions,
						equipmentTypeOptions,
						equipmentOptions,
						incubatorOptions,
						sterilizerOptions,
						biResultOptions
					});
				}
			})
			.finally(() => setState({ loading: false }));
	};
	useEffect(fetchData, []);

	return (
		<ReportForm
			activeRecord={state.activeRecord}
			onSubmit={handleSubmit}
			hideCancel
			saveBtnText="Run"
		/>
	);
};


export default Report;